import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { productsAPI, ordersAPI, categoriesAPI } from '../../services/api';
import './AdminPage.css';

type TabType = 'products' | 'orders' | 'categories';

interface Category {
    id: number;
    slug: string;
    name: string;
    description?: string;
    icon?: string;
    display_order: number;
}

interface Product {
    id: number;
    sku: string;
    name: string;
    description: string;
    short_description: string;
    category: string;
    price: number;
    discount_price?: number;
    images: string[];
    in_stock: number;
    display_order: number;
}

interface Order {
    id: number;
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    message: string;
    product_id: number | null;
    cart_items: string | null;
    status: string;
    created_at: string;
}

interface OrderCartItem {
    productId: string | number;
    name: string;
    category: string;
    quantity: number;
}

interface ProductFormData {
    sku: string;
    category: string;
    price: string;
    discount_price: string;
    in_stock: number;
    display_order: string;
    imageUrls: string[];
    imageFiles: File[];
    imageTypes: ('url' | 'file')[];  // тип каждого изображения
    translations: {
        en: { name: string; description: string; short_description: string };
        ru: { name: string; description: string; short_description: string };
        kk: { name: string; description: string; short_description: string };
    };
}

interface CategoryFormData {
    slug: string;
    icon: string;
    display_order: string;
    translations: {
        en: { name: string; description: string };
        ru: { name: string; description: string };
        kk: { name: string; description: string };
    };
}

const AdminPage: React.FC = () => {
    const { i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState<TabType>('products');
    const [products, setProducts] = useState<Product[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);

    // Product editing state
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState<ProductFormData>({
        sku: '',
        category: '',
        price: '',
        discount_price: '',
        in_stock: 1,
        display_order: '0',
        imageUrls: [''],
        imageFiles: [],
        imageTypes: ['url'],
        translations: {
            en: { name: '', description: '', short_description: '' },
            ru: { name: '', description: '', short_description: '' },
            kk: { name: '', description: '', short_description: '' }
        }
    });

    // Category editing state
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [categoryFormData, setCategoryFormData] = useState<CategoryFormData>({
        slug: '',
        icon: '📦',
        display_order: '0',
        translations: {
            en: { name: '', description: '' },
            ru: { name: '', description: '' },
            kk: { name: '', description: '' }
        }
    });

    useEffect(() => {
        loadData();
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, i18n.language]);

    const loadCategories = async () => {
        try {
            const response = await categoriesAPI.getAll(i18n.language);
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'products') {
                const response = await productsAPI.getAll({ language: i18n.language });
                setProducts(response.data.data);
            } else if (activeTab === 'orders') {
                const response = await ordersAPI.getAll({});
                setOrders(response.data.data);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            alert('Ошибка загрузки данных. Убедитесь, что вы авторизованы.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditProduct = async (product: Product) => {
        setEditingProduct(product);

        // Загружаем полные данные продукта со всеми переводами
        try {
            const response = await productsAPI.getWithTranslations(product.id);
            const fullProduct = response.data.data;

            const images = Array.isArray(fullProduct.images) ? fullProduct.images : [];
            const imageTypes = images.map((img: string) =>
                img.startsWith('http://') || img.startsWith('https://') ? 'url' : 'url'
            ) as ('url' | 'file')[];

            setFormData({
                sku: fullProduct.sku || '',
                category: fullProduct.category || '',
                price: fullProduct.price?.toString() || '',
                discount_price: fullProduct.discount_price?.toString() || '',
                in_stock: fullProduct.in_stock || 1,
                display_order: fullProduct.display_order?.toString() || '0',
                imageUrls: images.length > 0 ? images : [''],
                imageFiles: [],
                imageTypes: imageTypes.length > 0 ? imageTypes : ['url'],
                translations: fullProduct.translations || {
                    en: { name: '', description: '', short_description: '' },
                    ru: { name: '', description: '', short_description: '' },
                    kk: { name: '', description: '', short_description: '' }
                }
            });
            setIsModalOpen(true);
        } catch (error) {
            console.error('Error loading product details:', error);

            const status = (error as any)?.response?.status;
            if (status === 401 || status === 403) {
                localStorage.removeItem('isAdminAuthenticated');
                localStorage.removeItem('adminUser');
                localStorage.removeItem('admin_token');
                alert('Сессия истекла. Пожалуйста, войдите снова.');
                window.location.href = '/login';
                return;
            }

            alert('Ошибка загрузки данных продукта');
        }
    };

    const handleOpenCreateModal = () => {
        setEditingProduct(null);
        setFormData({
            sku: '',
            category: '',
            price: '',
            discount_price: '',
            in_stock: 1,
            display_order: String(products.length + 1),
            imageUrls: [''],
            imageFiles: [],
            imageTypes: ['url'],
            translations: {
                en: { name: '', description: '', short_description: '' },
                ru: { name: '', description: '', short_description: '' },
                kk: { name: '', description: '', short_description: '' }
            }
        });
        setIsModalOpen(true);
    };

    const handleCreateProduct = async () => {
        try {
            const createData = new FormData();
            createData.append('sku', formData.sku);
            createData.append('category', formData.category);
            createData.append('price', formData.price);
            if (formData.discount_price) {
                createData.append('discount_price', formData.discount_price);
            }
            createData.append('in_stock', formData.in_stock.toString());
            createData.append('display_order', formData.display_order);

            // Обработка изображений
            const imageData: any[] = [];
            let fileCounter = 0;

            formData.imageTypes.forEach((type, index) => {
                if (type === 'file' && formData.imageFiles[index]) {
                    createData.append('images', formData.imageFiles[index]);
                    imageData.push({ type: 'file', fileIndex: fileCounter });
                    fileCounter++;
                } else if (type === 'url' && formData.imageUrls[index]?.trim()) {
                    imageData.push({ type: 'url', url: formData.imageUrls[index].trim() });
                }
            });

            createData.append('imageData', JSON.stringify(imageData));
            createData.append('translations', JSON.stringify(formData.translations));

            await productsAPI.create(createData);
            alert('Продукт создан успешно!');
            setIsModalOpen(false);
            loadData();
        } catch (error) {
            console.error('Error creating product:', error);
            alert('Ошибка создания продукта: ' + (error as any).message);
        }
    };

    const handleSaveProduct = async () => {
        if (!editingProduct) {
            await handleCreateProduct();
            return;
        }

        try {
            const updateData = new FormData();
            updateData.append('sku', formData.sku);
            updateData.append('category', formData.category);
            updateData.append('price', formData.price);
            if (formData.discount_price) {
                updateData.append('discount_price', formData.discount_price);
            }
            updateData.append('in_stock', formData.in_stock.toString());
            updateData.append('display_order', formData.display_order);

            // Обработка изображений: файлы и URL
            const imageData: any[] = [];
            let fileCounter = 0;

            formData.imageTypes.forEach((type, index) => {
                if (type === 'file' && formData.imageFiles[index]) {
                    // Новый файл
                    updateData.append('images', formData.imageFiles[index]);
                    imageData.push({ type: 'file', fileIndex: fileCounter });
                    fileCounter++;
                } else if (type === 'url' && formData.imageUrls[index]?.trim()) {
                    // URL (новый или существующий)
                    imageData.push({ type: 'url', url: formData.imageUrls[index].trim() });
                }
            });

            console.log('Image data being sent:', imageData);
            console.log('Files count:', fileCounter);

            updateData.append('imageData', JSON.stringify(imageData));

            // Translations
            updateData.append('translations', JSON.stringify(formData.translations));

            const response = await productsAPI.update(editingProduct.id, updateData);
            console.log('Update response:', response);

            alert('Продукт обновлен успешно!');
            setIsModalOpen(false);
            setEditingProduct(null);
            loadData();
        } catch (error) {
            console.error('Error updating product:', error);
            alert('Ошибка обновления продукта: ' + (error as any).message);
        }
    };

    const handleDeleteProduct = async (id: number) => {
        if (window.confirm('Удалить этот продукт?')) {
            try {
                await productsAPI.delete(id);
                alert('Продукт удален');
                loadData();
            } catch (error) {
                console.error('Error:', error);
                alert('Ошибка удаления');
            }
        }
    };

    const handleUpdateOrderStatus = async (orderId: number, newStatus: string) => {
        try {
            await ordersAPI.updateStatus(orderId, newStatus);
            alert('Статус обновлен');
            setSelectedOrder(prev => prev && prev.id === orderId ? { ...prev, status: newStatus } : prev);
            loadData();
        } catch (error) {
            console.error('Error:', error);
            alert('Ошибка обновления статуса');
        }
    };

    const handleDeleteOrder = async (id: number) => {
        if (window.confirm('Удалить эту заявку?')) {
            try {
                await ordersAPI.delete(id);
                if (selectedOrder?.id === id) {
                    setSelectedOrder(null);
                    setIsOrderModalOpen(false);
                }
                alert('Заявка удалена');
                loadData();
            } catch (error) {
                console.error('Error:', error);
                alert('Ошибка удаления');
            }
        }
    };

    const updateFormField = (field: keyof ProductFormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const updateTranslation = (lang: 'en' | 'ru' | 'kk', field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            translations: {
                ...prev.translations,
                [lang]: {
                    ...prev.translations[lang],
                    [field]: value
                }
            }
        }));
    };

    const addImageUrl = () => {
        setFormData(prev => ({
            ...prev,
            imageUrls: [...prev.imageUrls, ''],
            imageFiles: [...prev.imageFiles],
            imageTypes: [...prev.imageTypes, 'url']
        }));
    };

    const updateImageUrl = (index: number, value: string) => {
        setFormData(prev => ({
            ...prev,
            imageUrls: prev.imageUrls.map((url, i) => i === index ? value : url)
        }));
    };

    const updateImageFile = (index: number, file: File | null) => {
        setFormData(prev => {
            const newFiles = [...prev.imageFiles];
            if (file) {
                newFiles[index] = file;
            }
            return {
                ...prev,
                imageFiles: newFiles
            };
        });
    };

    const updateImageType = (index: number, type: 'url' | 'file') => {
        setFormData(prev => ({
            ...prev,
            imageTypes: prev.imageTypes.map((t, i) => i === index ? type : t)
        }));
    };

    const removeImageUrl = (index: number) => {
        setFormData(prev => ({
            ...prev,
            imageUrls: prev.imageUrls.filter((_, i) => i !== index),
            imageFiles: prev.imageFiles.filter((_, i) => i !== index),
            imageTypes: prev.imageTypes.filter((_, i) => i !== index)
        }));
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'new': return 'status-new';
            case 'in_progress': return 'status-progress';
            case 'completed': return 'status-completed';
            case 'cancelled': return 'status-cancelled';
            default: return '';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'new': return 'Новая';
            case 'in_progress': return 'В работе';
            case 'completed': return 'Завершена';
            case 'cancelled': return 'Отменена';
            default: return status;
        }
    };

    const parseOrderCartItems = (order: Order): OrderCartItem[] => {
        if (!order.cart_items) return [];

        try {
            const parsed = JSON.parse(order.cart_items);
            if (!Array.isArray(parsed)) return [];

            return parsed
                .filter((item) => item && item.name)
                .map((item) => ({
                    productId: item.productId ?? '—',
                    name: item.name ?? 'Без названия',
                    category: item.category ?? 'Без категории',
                    quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1
                }));
        } catch (e) {
            return [];
        }
    };

    const openOrderModal = (order: Order) => {
        setSelectedOrder(order);
        setIsOrderModalOpen(true);
    };

    // Category management functions
    const openCategoryModal = (category: Category | null = null) => {
        if (category) {
            // Загружаем полную категорию с переводами
            categoriesAPI.getById(category.id).then(response => {
                const fullCategory = response.data;
                setEditingCategory(category);

                // Преобразуем массив переводов в объект
                const translationsObj: any = {
                    en: { name: '', description: '' },
                    ru: { name: '', description: '' },
                    kk: { name: '', description: '' }
                };

                if (fullCategory.translations && Array.isArray(fullCategory.translations)) {
                    fullCategory.translations.forEach((t: any) => {
                        translationsObj[t.language] = {
                            name: t.name || '',
                            description: t.description || ''
                        };
                    });
                }

                setCategoryFormData({
                    slug: fullCategory.slug || '',
                    icon: fullCategory.icon || '📦',
                    display_order: String(fullCategory.display_order || 0),
                    translations: translationsObj
                });
                setIsCategoryModalOpen(true);
            });
        } else {
            setEditingCategory(null);
            setCategoryFormData({
                slug: '',
                icon: '📦',
                display_order: String(categories.length + 1),
                translations: {
                    en: { name: '', description: '' },
                    ru: { name: '', description: '' },
                    kk: { name: '', description: '' }
                }
            });
            setIsCategoryModalOpen(true);
        }
    };

    const handleSaveCategory = async () => {
        try {
            const categoryData = {
                slug: categoryFormData.slug,
                icon: categoryFormData.icon,
                display_order: parseInt(categoryFormData.display_order),
                translations: categoryFormData.translations
            };

            if (editingCategory) {
                await categoriesAPI.update(editingCategory.id, categoryData);
                alert('Категория успешно обновлена!');
            } else {
                await categoriesAPI.create(categoryData);
                alert('Категория успешно создана!');
            }

            setIsCategoryModalOpen(false);
            loadCategories();
        } catch (error) {
            console.error('Error saving category:', error);
            alert('Ошибка при сохранении категории');
        }
    };

    const handleDeleteCategory = async (id: number) => {
        if (window.confirm('Вы уверены, что хотите удалить эту категорию? Это действие нельзя отменить.')) {
            try {
                await categoriesAPI.delete(id);
                alert('Категория успешно удалена!');
                loadCategories();
            } catch (error: any) {
                console.error('Error deleting category:', error);
                alert(error.response?.data?.message || 'Ошибка при удалении категории');
            }
        }
    };

    const updateCategoryField = (field: keyof CategoryFormData, value: any) => {
        setCategoryFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateCategoryTranslation = (lang: 'en' | 'ru' | 'kk', field: 'name' | 'description', value: string) => {
        setCategoryFormData(prev => ({
            ...prev,
            translations: {
                ...prev.translations,
                [lang]: {
                    ...prev.translations[lang],
                    [field]: value
                }
            }
        }));
    };

    return (
        <div className="admin-page">
            <div className="page-header">
                <h1>Панель управления</h1>
            </div>

            <div className="admin-tabs">
                <button
                    className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    📦 Продукты ({products.length})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                    onClick={() => setActiveTab('orders')}
                >
                    📋 Заявки ({orders.length})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
                    onClick={() => setActiveTab('categories')}
                >
                    🏷️ Категории ({categories.length})
                </button>
            </div>

            <div className="admin-container">
                {loading && <div className="loading-spinner">Загрузка...</div>}

                {/* Products Tab */}
                {activeTab === 'products' && !loading && (
                    <div className="products-section">
                        <div className="section-header">
                            <h2>Управление продуктами</h2>
                            <div className="section-header-actions">
                                <button className="add-product-btn" onClick={handleOpenCreateModal}>
                                    ➕ Добавить продукт
                                </button>
                                <p className="hint">Всего продуктов: {products.length}</p>
                            </div>
                        </div>
                        <div className="admin-products-grid">
                            {products.map((product) => {
                                const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;
                                return (
                                    <div key={product.id} className="admin-product-card">
                                        <div className="admin-product-image-wrapper">
                                            {firstImage ? (
                                                <img
                                                    src={firstImage}
                                                    alt={product.name}
                                                    className="admin-product-image"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = 'https://via.placeholder.com/150?text=No+Image';
                                                    }}
                                                />
                                            ) : (
                                                <div className="admin-product-no-image">Нет фото</div>
                                            )}
                                        </div>
                                        <div className="admin-product-info">
                                            <h3 className="admin-product-name">{product.name}</h3>
                                            <p className="admin-product-category">{product.category}</p>
                                            <p className="admin-product-price">${product.price}</p>
                                            <p className="admin-product-sku">SKU: {product.sku}</p>
                                        </div>
                                        <div className="admin-product-actions-inline">
                                            <button
                                                className="admin-edit-btn-inline"
                                                onClick={() => handleEditProduct(product)}
                                                title="Редактировать"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className="admin-delete-btn-inline"
                                                onClick={() => handleDeleteProduct(product.id)}
                                                title="Удалить"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && !loading && (
                    <div className="orders-section">
                        <div className="section-header">
                            <h2>Заявки с формы</h2>
                            <p className="hint">Всего заявок: {orders.length}</p>
                        </div>
                        <div className="orders-table-container">
                            <table className="orders-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Клиент</th>
                                        <th>Контакты</th>
                                        <th>Позиции</th>
                                        <th>Сообщение</th>
                                        <th>Дата</th>
                                        <th>Статус</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => {
                                        const cartItems = parseOrderCartItems(order);

                                        return (
                                            <tr key={order.id}>
                                                <td>#{order.id}</td>
                                                <td><strong>{order.customer_name}</strong></td>
                                                <td>
                                                    <div className="contact-info">
                                                        <div>📧 {order.customer_email || 'Не указан'}</div>
                                                        <div>📱 {order.customer_phone}</div>
                                                    </div>
                                                </td>
                                                <td>
                                                    {cartItems.length > 0 ? (
                                                        <div className="cart-items-preview">
                                                            {cartItems.map((item, idx) => (
                                                                <div key={idx} className="cart-item-row">
                                                                    <span className="cart-item-name">{item.name}</span>
                                                                    <span className="cart-item-qty">×{item.quantity}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="no-items">—</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="message-preview">
                                                        {order.message || 'Без сообщения'}
                                                    </div>
                                                </td>
                                                <td>{new Date(order.created_at).toLocaleDateString('ru-RU')}</td>
                                                <td>
                                                    <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                                                        {getStatusLabel(order.status)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="order-actions">
                                                        <button
                                                            className="view-order-btn"
                                                            onClick={() => openOrderModal(order)}
                                                            title="Открыть заявку"
                                                        >
                                                            Открыть
                                                        </button>
                                                        <select
                                                            value={order.status}
                                                            onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                                                            className="status-select"
                                                        >
                                                            <option value="new">Новая</option>
                                                            <option value="in_progress">В работе</option>
                                                            <option value="completed">Завершена</option>
                                                            <option value="cancelled">Отменена</option>
                                                        </select>
                                                        <button
                                                            className="delete-btn-small"
                                                            onClick={() => handleDeleteOrder(order.id)}
                                                            title="Удалить"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {isOrderModalOpen && selectedOrder && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOrderModalOpen(false)}
                    >
                        <motion.div
                            className="modal-content order-details-modal"
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2>Заявка #{selectedOrder.id}</h2>
                                <button className="modal-close" onClick={() => setIsOrderModalOpen(false)}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="order-modal-grid">
                                    <div className="order-modal-section">
                                        <h3>Информация по заявке</h3>
                                        <div className="order-meta-row">
                                            <span>Дата:</span>
                                            <strong>{new Date(selectedOrder.created_at).toLocaleString('ru-RU')}</strong>
                                        </div>
                                        <div className="order-meta-row">
                                            <span>Статус:</span>
                                            <span className={`status-badge ${getStatusBadgeClass(selectedOrder.status)}`}>
                                                {getStatusLabel(selectedOrder.status)}
                                            </span>
                                        </div>
                                        <div className="order-meta-row order-status-row">
                                            <span>Изменить статус:</span>
                                            <select
                                                value={selectedOrder.status}
                                                onChange={(e) => handleUpdateOrderStatus(selectedOrder.id, e.target.value)}
                                                className="status-select"
                                            >
                                                <option value="new">Новая</option>
                                                <option value="in_progress">В работе</option>
                                                <option value="completed">Завершена</option>
                                                <option value="cancelled">Отменена</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="order-modal-section">
                                        <h3>Клиент</h3>
                                        <div className="order-meta-row">
                                            <span>Имя:</span>
                                            <strong>{selectedOrder.customer_name}</strong>
                                        </div>
                                        <div className="order-meta-row">
                                            <span>Email:</span>
                                            <strong>{selectedOrder.customer_email || 'Не указан'}</strong>
                                        </div>
                                        <div className="order-meta-row">
                                            <span>Телефон:</span>
                                            <strong>{selectedOrder.customer_phone || 'Не указан'}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="order-modal-section">
                                    <h3>Сообщение заявки</h3>
                                    <div className="order-message-full">
                                        {selectedOrder.message || 'Сообщение не указано'}
                                    </div>
                                </div>

                                <div className="order-modal-section">
                                    <h3>Позиции</h3>
                                    {parseOrderCartItems(selectedOrder).length > 0 ? (
                                        <div className="order-items-list">
                                            {parseOrderCartItems(selectedOrder).map((item, idx) => (
                                                <div className="order-item-card" key={`${selectedOrder.id}-${idx}`}>
                                                    <div className="order-item-main">
                                                        <strong>{item.name}</strong>
                                                        <span className="order-item-category">{item.category}</span>
                                                    </div>
                                                    <div className="order-item-side">
                                                        <span className="order-item-qty">×{item.quantity}</span>
                                                        <span className="order-item-id">ID: {item.productId}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="no-items">Позиции не добавлены</div>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button className="delete-btn-small" onClick={() => handleDeleteOrder(selectedOrder.id)}>
                                    Удалить заявку
                                </button>
                                <button className="cancel-btn" onClick={() => setIsOrderModalOpen(false)}>
                                    Закрыть
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Edit Product Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsModalOpen(false)}
                    >
                        <motion.div
                            className="modal-content"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2>{editingProduct ? 'Редактировать продукт' : 'Создать продукт'}</h2>
                                <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="form-grid">
                                    {/* Basic Info */}
                                    <div className="form-section">
                                        <h3>Основная информация</h3>
                                        <div className="form-row">
                                            <div className="form-field">
                                                <label>SKU</label>
                                                <input
                                                    type="text"
                                                    value={formData.sku}
                                                    onChange={(e) => updateFormField('sku', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-field full-width">
                                            <label>Категории (можно выбрать несколько)</label>
                                            <div className="category-checkboxes">
                                                {categories.map(cat => {
                                                    const selectedCategories = formData.category.split(',').map(c => c.trim()).filter(Boolean);
                                                    const isChecked = selectedCategories.includes(cat.slug);
                                                    return (
                                                        <label key={cat.slug} className="category-checkbox-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    let newCategories = [...selectedCategories];
                                                                    if (e.target.checked) {
                                                                        newCategories.push(cat.slug);
                                                                    } else {
                                                                        newCategories = newCategories.filter(c => c !== cat.slug);
                                                                    }
                                                                    updateFormField('category', newCategories.join(', '));
                                                                }}
                                                            />
                                                            <span>{cat.icon} {cat.name}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-field">
                                                <label>Цена ($)</label>
                                                <input
                                                    type="number"
                                                    value={formData.price}
                                                    onChange={(e) => updateFormField('price', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Цена со скидкой ($)</label>
                                                <input
                                                    type="number"
                                                    value={formData.discount_price}
                                                    onChange={(e) => updateFormField('discount_price', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-field">
                                                <label>В наличии</label>
                                                <select
                                                    value={formData.in_stock}
                                                    onChange={(e) => updateFormField('in_stock', parseInt(e.target.value))}
                                                >
                                                    <option value={1}>Да</option>
                                                    <option value={0}>Нет</option>
                                                </select>
                                            </div>
                                            <div className="form-field">
                                                <label>Порядок отображения</label>
                                                <input
                                                    type="number"
                                                    value={formData.display_order}
                                                    onChange={(e) => updateFormField('display_order', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Images */}
                                    <div className="form-section">
                                        <h3>Изображения</h3>
                                        {formData.imageTypes.map((type, index) => (
                                            <div key={index} className="image-input-group">
                                                <div className="image-type-selector">
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            checked={type === 'url'}
                                                            onChange={() => updateImageType(index, 'url')}
                                                        />
                                                        URL
                                                    </label>
                                                    <label>
                                                        <input
                                                            type="radio"
                                                            checked={type === 'file'}
                                                            onChange={() => updateImageType(index, 'file')}
                                                        />
                                                        Файл
                                                    </label>
                                                </div>
                                                {type === 'url' ? (
                                                    <input
                                                        type="text"
                                                        placeholder="https://example.com/image.jpg"
                                                        value={formData.imageUrls[index] || ''}
                                                        onChange={(e) => updateImageUrl(index, e.target.value)}
                                                        className="image-url-input"
                                                    />
                                                ) : (
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0] || null;
                                                            updateImageFile(index, file);
                                                        }}
                                                        className="image-file-input"
                                                    />
                                                )}
                                                {formData.imageTypes.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="remove-image-btn"
                                                        onClick={() => removeImageUrl(index)}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        <button type="button" className="add-image-btn" onClick={addImageUrl}>
                                            + Добавить изображение
                                        </button>
                                    </div>

                                    {/* Translations */}
                                    <div className="form-section full-width">
                                        <h3>Переводы</h3>

                                        {/* Russian */}
                                        <div className="translation-group">
                                            <h4>🇷🇺 Русский</h4>
                                            <div className="form-field">
                                                <label>Название</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations.ru.name}
                                                    onChange={(e) => updateTranslation('ru', 'name', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Краткое описание</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations.ru.short_description}
                                                    onChange={(e) => updateTranslation('ru', 'short_description', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Полное описание</label>
                                                <textarea
                                                    rows={3}
                                                    value={formData.translations.ru.description}
                                                    onChange={(e) => updateTranslation('ru', 'description', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* English */}
                                        <div className="translation-group">
                                            <h4>🇬🇧 English</h4>
                                            <div className="form-field">
                                                <label>Name</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations.en.name}
                                                    onChange={(e) => updateTranslation('en', 'name', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Short Description</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations.en.short_description}
                                                    onChange={(e) => updateTranslation('en', 'short_description', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Full Description</label>
                                                <textarea
                                                    rows={3}
                                                    value={formData.translations.en.description}
                                                    onChange={(e) => updateTranslation('en', 'description', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Kazakh */}
                                        <div className="translation-group">
                                            <h4>🇰🇿 Қазақша</h4>
                                            <div className="form-field">
                                                <label>Атауы</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations.kk.name}
                                                    onChange={(e) => updateTranslation('kk', 'name', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Қысқаша сипаттама</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations.kk.short_description}
                                                    onChange={(e) => updateTranslation('kk', 'short_description', e.target.value)}
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Толық сипаттама</label>
                                                <textarea
                                                    rows={3}
                                                    value={formData.translations.kk.description}
                                                    onChange={(e) => updateTranslation('kk', 'description', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button className="cancel-btn" onClick={() => setIsModalOpen(false)}>
                                    Отмена
                                </button>
                                <button className="save-btn" onClick={handleSaveProduct}>
                                    {editingProduct ? 'Сохранить изменения' : 'Создать продукт'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Categories Tab */}
            {activeTab === 'categories' && !loading && (
                <div className="categories-section">
                    <div className="section-header">
                        <h2>Управление категориями</h2>
                        <button className="add-btn" onClick={() => openCategoryModal()}>
                            ➕ Добавить категорию
                        </button>
                    </div>
                    <div className="categories-list">
                        {categories.map((category) => (
                            <div key={category.id} className="category-card">
                                {/* <div className="category-icon">{category.icon}</div> */}
                                <div className="category-info">
                                    <h3>{category.name}</h3>
                                    <p className="category-slug">slug: {category.slug}</p>
                                    {category.description && (
                                        <p className="category-description">{category.description}</p>
                                    )}
                                </div>
                                <div className="category-actions">
                                    <div className="category-order">Порядок: {category.display_order}</div>
                                    <div className="action-buttons">
                                        <button
                                            className="edit-btn-small"
                                            onClick={() => openCategoryModal(category)}
                                            title="Редактировать"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="delete-btn-small"
                                            onClick={() => handleDeleteCategory(category.id)}
                                            title="Удалить"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Category Modal */}
            <AnimatePresence>
                {isCategoryModalOpen && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsCategoryModalOpen(false)}
                    >
                        <motion.div
                            className="modal-content category-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h2>{editingCategory ? '✏️ Редактировать категорию' : '➕ Создать категорию'}</h2>
                                <button className="close-btn" onClick={() => setIsCategoryModalOpen(false)}>✕</button>
                            </div>

                            <div className="modal-body">
                                <div className="category-form">
                                    <div className="form-section">
                                        <h3>Основная информация</h3>
                                        <div className="form-row">
                                            <div className="form-field">
                                                <label>Slug (уникальный идентификатор)</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.slug}
                                                    onChange={(e) => updateCategoryField('slug', e.target.value)}
                                                    placeholder="business-class"
                                                />
                                                <small>Используйте латиницу и дефисы</small>
                                            </div>
                                            <div className="form-field">
                                                <label>Иконка (эмодзи)</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.icon}
                                                    onChange={(e) => updateCategoryField('icon', e.target.value)}
                                                    placeholder="✈️"
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Порядок отображения</label>
                                                <input
                                                    type="number"
                                                    value={categoryFormData.display_order}
                                                    onChange={(e) => updateCategoryField('display_order', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="translations-section">
                                        <h3>Переводы</h3>

                                        <div className="translation-group">
                                            <h4>🇬🇧 English</h4>
                                            <div className="form-field">
                                                <label>Название</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.translations.en.name}
                                                    onChange={(e) => updateCategoryTranslation('en', 'name', e.target.value)}
                                                    placeholder="Business Class"
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Описание</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.translations.en.description}
                                                    onChange={(e) => updateCategoryTranslation('en', 'description', e.target.value)}
                                                    placeholder="Premium meals for business travelers"
                                                />
                                            </div>
                                        </div>

                                        <div className="translation-group">
                                            <h4>🇷🇺 Русский</h4>
                                            <div className="form-field">
                                                <label>Название</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.translations.ru.name}
                                                    onChange={(e) => updateCategoryTranslation('ru', 'name', e.target.value)}
                                                    placeholder="Бизнес-класс"
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Описание</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.translations.ru.description}
                                                    onChange={(e) => updateCategoryTranslation('ru', 'description', e.target.value)}
                                                    placeholder="Премиальное питание для бизнес-пассажиров"
                                                />
                                            </div>
                                        </div>

                                        <div className="translation-group">
                                            <h4>🇰🇿 Қазақша</h4>
                                            <div className="form-field">
                                                <label>Название</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.translations.kk.name}
                                                    onChange={(e) => updateCategoryTranslation('kk', 'name', e.target.value)}
                                                    placeholder="Бизнес-класс"
                                                />
                                            </div>
                                            <div className="form-field">
                                                <label>Описание</label>
                                                <input
                                                    type="text"
                                                    value={categoryFormData.translations.kk.description}
                                                    onChange={(e) => updateCategoryTranslation('kk', 'description', e.target.value)}
                                                    placeholder="Бизнес жолаушыларға арналған премиум тағамдар"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button className="cancel-btn" onClick={() => setIsCategoryModalOpen(false)}>
                                    Отмена
                                </button>
                                <button className="save-btn" onClick={handleSaveCategory}>
                                    {editingCategory ? 'Сохранить изменения' : 'Создать категорию'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminPage;
